import { Palette, Globe2, Smartphone, Bitcoin, Boxes, Blocks, FileCode2, Network, Hexagon, Layers, Zap, LayoutGrid, Rocket, CircleDollarSign } from "lucide-react";

export const SERVICES = [
  {
    slug: "website-designing",
    num: "01",
    name: "Website Designing",
    tagline: "Digital flagships that win attention",
    headline: "Awwwards-Calibre Websites & Interactive Brand Experiences",
    icon: Palette,
    image:
      "https://images.unsplash.com/photo-1541462608143-67571c6738dd?crop=entropy&cs=srgb&fm=jpg&q=85",
    excerpt:
      "Kinetic, conversion-focused websites that make your brand impossible to ignore.",
    about: [
      "Your website is your hardest-working salesperson. We design digital flagships — not templates — with kinetic typography, purposeful motion and editorial layouts that hold attention from the first pixel.",
      "Every SparkGrid website is engineered for speed, search visibility and conversion. From immersive landing pages to complete corporate platforms, we obsess over the details that turn visitors into customers.",
    ],
    deliverables: [
      "Custom UI/UX design in Figma",
      "Kinetic hero & motion design",
      "Responsive development (mobile-first)",
      "SEO architecture & Core Web Vitals",
      "CMS setup for easy edits",
      "Analytics & conversion tracking",
    ],
    stack: ["Figma", "React", "Next.js", "Tailwind CSS", "Framer Motion", "GSAP"],
  },
  {
    slug: "web-app-development",
    num: "02",
    name: "Web App Development",
    tagline: "Cloud-grade platforms, engineered to scale",
    headline: "High-Performance Web Applications & Enterprise Portals",
    icon: Globe2,
    image:
      "https://images.unsplash.com/photo-1629904853716-f0bc54eea481?crop=entropy&cs=srgb&fm=jpg&q=85",
    excerpt:
      "Dashboards, portals and SaaS platforms built on rock-solid cloud architecture.",
    about: [
      "From internal tools to customer-facing SaaS, we build web applications that stay fast under real-world load. Clean architecture, typed APIs and automated testing come standard — never as add-ons.",
      "Our Mumbai engineering team ships with CI/CD pipelines, observability and security baked in, so your product scales from ten users to ten lakh without a rewrite.",
    ],
    deliverables: [
      "Product architecture & technical roadmap",
      "Role-based dashboards & admin panels",
      "REST / GraphQL API development",
      "Payment & third-party integrations",
      "Cloud deployment on AWS / GCP",
      "Automated testing & CI/CD pipelines",
    ],
    stack: ["React", "Node.js", "PostgreSQL", "Redis", "AWS", "Docker"],
  },
  {
    slug: "app-development",
    num: "03",
    name: "App Development",
    tagline: "Pocket-sized experiences, native feel",
    headline: "Native & Cross-Platform Apps with Seamless UX",
    icon: Smartphone,
    image:
      "https://images.unsplash.com/photo-1526038335545-4b96864eaee7?crop=entropy&cs=srgb&fm=jpg&q=85",
    excerpt:
      "iOS & Android apps your users will love — from MVP to millions of installs.",
    about: [
      "We craft mobile apps that feel effortless — buttery animations, instant load times and offline-first reliability. Whether you need a lean MVP or a full consumer platform, we ship apps that earn five-star reviews.",
      "Flutter and React Native let us move fast with one codebase; when raw performance matters, we go fully native with Swift and Kotlin.",
    ],
    deliverables: [
      "iOS & Android app development",
      "Flutter / React Native cross-platform builds",
      "App Store & Play Store launch management",
      "Push notifications & deep linking",
      "Offline-first data sync",
      "Crash analytics & performance monitoring",
    ],
    stack: ["Flutter", "React Native", "Swift", "Kotlin", "Firebase", "Fastlane"],
  },
  {
    slug: "crypto-products",
    num: "04",
    name: "Crypto Products",
    tagline: "Web3, minus the hype — built to last",
    headline: "Decentralised Apps, Smart Contracts & Web3 Ecosystems",
    icon: Bitcoin,
    image:
      "https://images.unsplash.com/photo-1643000296927-f4f1c8722b7d?crop=entropy&cs=srgb&fm=jpg&q=85",
    excerpt:
      "Smart contracts, DeFi tooling, wallets and token platforms — audited and secure.",
    about: [
      "Web3 rewards the careful. We design and build crypto products with security-first smart contract development, gas-optimised code and interfaces that make decentralised tech feel simple.",
      "From token launches and NFT platforms to DeFi dashboards and custody integrations, our team has shipped across Ethereum, Polygon and Solana ecosystems.",
    ],
    deliverables: [
      "Smart contract development & audit prep",
      "dApp frontend with wallet connect",
      "Token & NFT platform engineering",
      "DeFi dashboards & analytics",
      "Exchange / custody API integrations",
      "Testnet-to-mainnet deployment strategy",
    ],
    stack: ["Solidity", "Ethereum", "Polygon", "Hardhat", "wagmi", "The Graph"],
    subServices: [
      { name: "Blockchain Development", icon: Blocks, text: "End-to-end public and private blockchain solutions engineered for scale, security and real-world adoption." },
      { name: "Smart Contract Development", icon: FileCode2, text: "Secure, gas-optimised contracts in Solidity and Rust — written, tested and audit-ready from day one." },
      { name: "Custom Blockchain Development", icon: Network, text: "Purpose-built chains, consensus layers and protocols tailored precisely to your business logic." },
      { name: "BSC Blockchain Development", icon: Hexagon, text: "High-throughput products on BNB Chain infrastructure with ultra-low transaction fees." },
      { name: "Polygon Blockchain Development", icon: Layers, text: "Ethereum-compatible scaling with near-zero gas fees, instant finality and massive liquidity access." },
      { name: "Binance Smart Chain (BSC)", icon: Zap, text: "DeFi platforms, DEXs, yield protocols and launchpads built for the BSC ecosystem." },
      { name: "DApp Development", icon: LayoutGrid, text: "Full-stack decentralised applications with wallet connect, intuitive UX and robust backends." },
      { name: "Solana DApp Development", icon: Rocket, text: "Blazing-fast dApps on Solana — Rust programs, sub-second finality and negligible fees." },
      { name: "Token Development", icon: CircleDollarSign, text: "Utility, governance and meme tokens — tokenomics design, contract, audit prep and launch support." },
    ],
  },
  {
    slug: "custom-software",
    num: "05",
    name: "Customized Products",
    tagline: "If you can dream it, we can ship it",
    headline: "Bespoke SaaS, AI Tools & Complex System Integrations",
    icon: Boxes,
    image:
      "https://images.unsplash.com/photo-1639825988283-39e5408b75e8?crop=entropy&cs=srgb&fm=jpg&q=85",
    excerpt:
      "AI tools, automation systems and one-of-a-kind products tailored to your business.",
    about: [
      "Some ideas don't fit a category — that's where we do our best work. Tell us the problem, and we'll architect a product around it: AI assistants, workflow automation, ERP connectors, IoT dashboards and more.",
      "We work as your product engineering partner, not a vendor. Weekly demos, transparent pricing and code you fully own from day one.",
    ],
    deliverables: [
      "Discovery workshop & product spec",
      "AI / LLM-powered feature development",
      "Legacy system & API integrations",
      "Workflow automation engines",
      "White-label SaaS development",
      "Dedicated support & iteration sprints",
    ],
    stack: ["Python", "FastAPI", "OpenAI", "LangChain", "PostgreSQL", "Docker"],
  },
];
